import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, MapPin, Calendar, Clock, Users, Download, Sparkles } from "lucide-react";
import { Layout } from "../components/Layout";
import { Logo } from "../components/Logo";
import { useI18n } from "../i18n";
import { eur } from "../lib/format";
import * as apiClient from "../api";

export default function Voucher() {
  const { bookingNumber } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t");
  const { t } = useI18n();
  const { data: booking, isLoading } = useQuery({ queryKey: ["booking", bookingNumber, token], queryFn: () => apiClient.getBooking(bookingNumber, token) });

  if (isLoading || !booking) return <Layout><div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-400">{t("common.loading")}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8 animate-float-up">
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900">{t("voucher.title")}</h1>
          <p className="text-slate-500 mt-2">{t("voucher.number")}: <span className="font-bold text-primary" data-testid="voucher-number">{booking.booking_number}</span></p>
        </div>

        <div className="rounded-3xl border border-slate-100 shadow-lg overflow-hidden bg-white" data-testid="voucher-card">
          <div className="bg-primary text-white p-6 flex items-center justify-between">
            <Logo light />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Voucher</div>
              <div className="font-heading font-bold">{booking.booking_number}</div>
            </div>
          </div>

          <div className="p-6 grid sm:grid-cols-3 gap-6">
            <div className="sm:col-span-1 flex flex-col items-center justify-center">
              <img src={booking.qr} alt="QR Code" className="h-40 w-40" data-testid="voucher-qr" />
              <p className="text-xs text-slate-400 mt-2 text-center">{t("voucher.showqr")}</p>
            </div>
            <div className="sm:col-span-2 space-y-4">
              {booking.items.map((it) => (
                <div key={`${it.experience_id}-${it.date}-${it.time}`} className="rounded-2xl border border-slate-100 p-4">
                  <h3 className="font-heading font-semibold text-slate-900">{it.title}</h3>
                  <p className="text-xs text-slate-400 mb-2">{t("exp.operator")}: {it.operator_name}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-primary" />{it.date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{it.time}</span>
                    <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" />{it.participants} {t("common.participants")}</span>
                    <span className="font-bold text-primary">{eur(it.subtotal)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />{it.meeting_point}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 p-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-400">{t("cart.total")}</div>
              <div className="text-2xl font-heading font-bold text-primary">{eur(booking.gmv)}</div>
            </div>
            <div className="text-right text-xs text-slate-400">
              <div>Cristalda Experience</div>
              <div>Centro Storico di Vieste</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button onClick={() => window.print()} data-testid="download-voucher-btn" className="flex-1 rounded-full bg-primary text-white font-semibold py-3 hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"><Download className="h-4 w-4" /> {t("voucher.download")}</button>
          <Link to="/concierge" className="flex-1 rounded-full border border-primary text-primary font-semibold py-3 hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"><Sparkles className="h-4 w-4" /> {t("nav.concierge")}</Link>
        </div>
      </div>
    </Layout>
  );
}
